import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import CasinoCard from '../components/CasinoCard';
import ApiService from '../services/api';
import { mockCasinos } from '../data/mockCasinos';
import sharedData from '../services/sharedData';

const HomePage = () => {
  const [casinos, setCasinos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [usingMockData, setUsingMockData] = useState(false);
  const [dataSource, setDataSource] = useState('loading');

  useEffect(() => {
    fetchCasinos();
    
    // Écouter les changements de données (depuis admin)
    const unsubscribe = sharedData.onDataChanged((data) => {
      console.log('Data changed detected, updating casinos');
      if (data.casinos) {
        // Sort by rating descending
        const sortedData = [...data.casinos].sort((a, b) => b.rating - a.rating);
        setCasinos(sortedData);
        setUsingMockData(true);
        setDataSource('shared');
      }
    });
    
    // Écouter les changements dans d'autres onglets
    const unsubscribeStorage = sharedData.onStorageChanged((data) => {
      if (data) {
        console.log('Storage changed detected, updating casinos');
        const sortedData = [...data].sort((a, b) => b.rating - a.rating);
        setCasinos(sortedData);
        setUsingMockData(true);
        setDataSource('shared');
      }
    });

    return () => {
      unsubscribe();
      unsubscribeStorage();
    };
  }, []);

  const fetchCasinos = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // D'abord, vérifier s'il y a des données mises à jour dans le localStorage
      if (sharedData.hasUpdatedData()) {
        console.log('Using shared data from localStorage');
        const sharedCasinos = sharedData.getCasinos();
        const sortedData = [...sharedCasinos].sort((a, b) => b.rating - a.rating);
        setCasinos(sortedData);
        setUsingMockData(true);
        setDataSource('shared');
        setLoading(false);
        return;
      }
      
      // Essayer l'API en premier
      console.log('Attempting to fetch from API...');
      const data = await ApiService.getCasinos('rating');
      setCasinos(data);
      setUsingMockData(false);
      setDataSource('api');
      console.log('Successfully loaded data from API');
    } catch (err) {
      console.error('API failed, falling back to mock data:', err);
      
      // Fallback vers les données mock
      const sortedMockData = [...mockCasinos].sort((a, b) => b.rating - a.rating);
      setCasinos(sortedMockData);
      setUsingMockData(true);
      setDataSource('mock');
      setError(null); // Clear error since we have fallback data
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-16 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement des offres de paris...</p>
        </div>
      </div>
    );
  }

  const visibleCasinos = casinos.filter(c => !c.hidden);

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-900 to-blue-700 text-white py-8 md:py-12">
        <div className="container mx-auto px-4 text-center">
          <p className="text-sm text-blue-200 mb-2">Mis à jour le {new Date().toLocaleDateString('fr-FR')}</p>
          <h1 className="text-2xl md:text-4xl font-bold mb-4">Les Meilleurs Sites de Paris Sportifs</h1>
          <p className="text-blue-100 max-w-2xl mx-auto text-sm md:text-base">
            Nous avons sélectionné pour vous les meilleurs bookmakers avec les offres de bienvenue les plus généreuses !
          </p>
          
          {/* Trust badges */}
          <div className="flex flex-wrap justify-center gap-4 md:gap-8 mt-6">
            <div className="flex items-center space-x-2 bg-white/10 px-3 py-2 rounded-lg">
              <span className="text-green-400">✓</span>
              <span className="text-sm">Agréés ANJ</span>
            </div>
            <div className="flex items-center space-x-2 bg-white/10 px-3 py-2 rounded-lg">
              <span className="text-yellow-400">★</span>
              <span className="text-sm">Bonus Exclusifs</span>
            </div>
            <div className="flex items-center space-x-2 bg-white/10 px-3 py-2 rounded-lg">
              <span className="text-blue-300">⚡</span>
              <span className="text-sm">Retraits Rapides</span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Table header */}
      <div className="bg-white border-b shadow-sm sticky top-0 z-10">
        <div className="container mx-auto px-4">
          <div className="hidden md:grid md:grid-cols-12 gap-4 py-3 text-xs font-semibold text-gray-500 uppercase">
            <div className="col-span-3">Site</div>
            <div className="col-span-2 text-center">Dépôt Mini</div>
            <div className="col-span-5">Offre de Bienvenue</div>
            <div className="col-span-1 text-center">Score</div>
            <div className="col-span-1"></div>
          </div>
        </div>
      </div>
      
      {/* Casino listings - filter out hidden ones */}
      <div className="container mx-auto px-4 py-4">
        {visibleCasinos.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-gray-600 text-lg">Aucune offre de paris disponible pour le moment.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {visibleCasinos.map((casino, index) => (
              <CasinoCard key={casino.id} casino={casino} rank={index + 1} />
            ))}
          </div>
        )}
      </div>
      
      {/* Footer */}
      <Footer />
    </div>
  );
};

export default HomePage;